import React, { useEffect, useState } from "react";
import { Box, Input, Modal, Button } from "@sainsburys-tech/fable";
import Headerfile from "../../common/Headerfile";
import Footerfile from "../../common/Footerfile";
import Styles from "./AddPrinterModel.module.css";
import { useNavigate } from "react-router-dom";
import { useGetPostsQuery, useNewPostMutation } from "../../../api/apiConfig";
import { getLogisticsRole } from "../../../utils/authToken";
import { USER_ROLE } from "../../../constants/roles";
import Unauthorized from "../../unauthorized/Unauthorized";

const AddPrinterModel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const {
    isLoading,
    data: getData,
    refetch
  } = useGetPostsQuery(`printer-models/${page}/${rowsPerPage}`);
  const [newPost] = useNewPostMutation();
  const [selectedPrinter, setSelectedPrinter] = useState({
    Name: "",
    Image: "",
    Manufacturer: ""
  });
  const [apiHeading, setApiHeading] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [isResponseOpen, setResponseOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

  useEffect(() => {
    refetch();
  }, []);

  const initialState: IState = {
    Name: "",
    Image: null,
    Manufacturer: ""
  };

  interface IState {
    Name: string;
    Image: File | null;
    Manufacturer: string;
  }

  const [values, setValues] = useState<IState>({
    Name: "",
    Image: null,
    Manufacturer: ""
  });

  const [error, setError] = useState<{
    Name: string;
    Image: string;
    Manufacturer: string;
  }>({
    Name: "",
    Image: "",
    Manufacturer: ""
  });

  const navigate = useNavigate();

  const handleChange = (name: string, value: string | File) => {
    setValues({ ...values, [name]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleChange("Image", e.target.files[0]);
    }
  };

  interface IData {
    StatusCode: number;
    Status: string;
    TotalRecords?: number | null;
    Message?: string;
    Model?: any;
    Value?: string;
  }

  interface IResponse {
    data: IData;
    TotalRecords?: number | null;
  }

  function isIResponse(response: any): response is IResponse {
    return response.data !== undefined && typeof response.data === "object";
  }

  const isBlob = (value: any): value is Blob => {
    return value instanceof Blob;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitted(true);
    const validationErrors = Validation(values);
    setError(validationErrors);
    if (
      validationErrors?.Name?.length === 0 &&
      values?.Name !== "" &&
      validationErrors.Image.length === 0
    ) {
      setIsSaveButtonDisabled(true);
      if (values.Image && isBlob(values.Image)) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          if (reader.result) {
            const base64String = reader.result
              .toString()
              .replace("data:", "")
              .replace(/^.+,/, "");

            try {
              const response = await newPost({
                Method: "printer-models",
                ...values,
                Image: base64String,
                  ManufacturerName: values.Manufacturer,
                Id: ""
              });
              if (
                isIResponse(response) &&
                response.data.StatusCode === 0 &&
                response.data.Status === "Ok"
              ) {
                setIsSaveButtonDisabled(false);
                setApiMessage("Printer model created successfully" || null);
                setApiHeading("Success!!!");
                setResponseOpen(true);
                setFormKey(formKey + 1);
                setValues(initialState);
                refetch();
              } else if (isIResponse(response) && response.data) {
                setApiMessage(response.data.Message || null);
                setApiHeading("Error!!!");
                setResponseOpen(true);
                console.error("Error response:", response);
                setIsSaveButtonDisabled(false);
              } else {
                console.error("Something Went Wrong..", response);
                setIsSaveButtonDisabled(false);
              }
            } catch (error) {
              console.error(error);
              setIsSaveButtonDisabled(false);
            }
          } else {
            console.error("FileReader result is null");
          }
        };
        reader.readAsDataURL(values.Image);
      }
    }
  };

  const Validation = (values: IState) => {
    let error = { Name: "", Image: "", Manufacturer: "" };

    if (!values.Name) {
      error.Name = "Printer Model is required";
    }

    if (!values.Image) {
      error.Image = "Printer Image is required";
    }

    if (values.Image) {
      const fileType = values.Image.type;
      const fileSize = values.Image.size / 1024 / 1024; // size in MB
      const validImageTypes = ["image/jpeg", "image/png"];

      if (!validImageTypes.includes(fileType)) {
        error.Image = "Only JPG and PNG files are allowed";
      } else if (fileSize > 2) {
        error.Image = "File size should be less than 2MB";
      }
    }

    return error;
  };

  const handleView = (Name: string, Image: string, Manufacturer: string) => {
    setSelectedPrinter({ Name, Image, Manufacturer });
    setIsOpen(true);
  };

  const handleEdit = (id: string) => {
    navigate(`/editprintermodel/${id}`);
  };

  const loggedUserRole = getLogisticsRole();

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
    loggedUserRole?.includes(USER_ROLE.READ_WRITE)
      ? false
      : true;

  const handleNextPage = () => {
    if (getData && page < Math.ceil(getData.TotalRecords / rowsPerPage)) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (getData && page > 1) {
      setPage(page - 1);
    }
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  return (
    <div>
      <div className={Styles.container}>
        <Headerfile />
        {isDisabled ? (
          <Unauthorized />
        ) : (
          <Box as="div" className={Styles.box}>
            <form
              data-testid={"form"}
              key={formKey}
              onSubmit={handleSubmit}
              className={Styles.formContainer}
            >
              <h5 className={Styles.heading}>Add Printer Model</h5>
              <br />
              <p className={Styles.rightAlign}>
                <span className={Styles.redAsterisk}>*</span>Indicates required
                field
              </p>
              <div>
                <label className={Styles.label}>
                  <Input
                    name="Name"
                    label="Printer Model*"
                    autocomplete="off"
                    type="text"
                    onChange={(e) => handleChange("Name", e)}
                  />
                </label>
                <span
                  className={Styles.errormessage}
                  data-testid="error-Name"
                  style={{
                    display: isSubmitted && error.Name ? "block" : "none"
                  }}
                >
                  {error.Name}
                </span>
                <br />
                <label className={`${Styles.label} ${Styles.fileLabel}`}>
                  Printer Image*
                </label>
                <input
                  name="Image"
                  className={`${Styles.fileInput} form-control form-control-lg`}
                  id="formFileLg"
                  type="file"
                  onChange={handleFileChange}
                />
                <span
                  className={Styles.errormessage}
                  data-testid="error-Image"
                  style={{
                    display: isSubmitted && error.Image ? "block" : "none"
                  }}
                >
                  {error.Image}
                </span>
                <br />
                <label className={Styles.label}>
                  <Input
                    name="Manufacturer"
                    label="Manufacturer"
                    autocomplete="off"
                    type="text"
                    onChange={(e) => handleChange("Manufacturer", e)}
                  />
                </label>

                <br />
                <Button
                  type="submit"
                  className={Styles.button}
                  disabled={isSaveButtonDisabled}
                >
                  Save
                </Button>
              </div>
            </form>

            <div className={Styles.tableContainer}>
              <h5 className={Styles.heading}>View Printer Model</h5>
              {isLoading ? (
                <div className={Styles.loadingContainer}>
                  <div className={Styles.loadingSpinner}></div>
                  <div className={Styles.loadingText}>Loading...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-borderless">
                    <thead>
                      <tr>
                        <th>Printer Model</th>
                        <th>Printer Image</th>
                        <th>Manufacturer</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getData && getData?.Model?.length > 0 ? (
                        getData?.Model.map((printerModel: any) => (
                          <tr
                            key={printerModel.PrinterModelId ?? printerModel.Id}
                          >
                            <td>{printerModel?.Name}</td>
                            <td>
                              {printerModel?.Image ? (
                                <img
                                  alt={printerModel.Image}
                                  src={`data:image/jpeg;base64,${printerModel.Image}`}
                                  height="20%"
                                  width="30%"
                                />
                              ) : (
                                "Not Available"
                              )}
                            </td>
                            <td>{printerModel.ManufacturerName}</td>
                            <td className="text-center">
                              {" "}
                              <button
                                style={{ background: "#007bff" }}
                                className="btn btn-primary mx-1"
                                onClick={() =>
                                  handleView(
                                    printerModel?.Name,
                                    printerModel?.Image ?? "",
                                    printerModel?.ManufacturerName ?? ""
                                  )
                                }
                              >
                                View
                              </button>
                              <button
                                style={{ background: "#808080" }}
                                className="btn btn-primary"
                                onClick={() =>
                                  handleEdit(
                                    printerModel.PrinterModelId ??
                                      printerModel.Id ??
                                      ""
                                  )
                                }
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className={Styles.printerNotFound}>
                          <td colSpan={14} className="text-center">
                            No Printer Model Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className={Styles.pagination}>
                    <button onClick={handlePreviousPage} disabled={page === 1}>
                      Previous
                    </button>
                    <span>
                      Page {page} of{" "}
                      {getData
                        ? Math.ceil(getData.TotalRecords / rowsPerPage)
                        : 0}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={
                        !getData ||
                        page === Math.ceil(getData.TotalRecords / rowsPerPage)
                      }
                    >
                      Next
                    </button>
                  </div>
                  <div className={Styles.rowsPerPage}>
                    <label htmlFor="rowsPerPage">Rows per page:</label>
                    <select
                      id="rowsPerPage"
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={40}>40</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
            <Modal
              isOpen={isResponseOpen}
              onClose={() => setResponseOpen(false)}
              layout="default"
              header={{
                isDismissible: true
              }}
            >
              <div className="text-center fw-400">
                <h3 className="text-center fw-500">{apiHeading}</h3>
                <h5 className="fw-400">{apiMessage}</h5>
              </div>
            </Modal>

            <Modal
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              layout="default"
              header={{
                isDismissible: true
              }}
            >
              <div className="text-center">
                <h3>{selectedPrinter?.Name}</h3>
                {selectedPrinter?.Image ? (
                  <img
                    src={`data:image/jpeg;base64,${selectedPrinter.Image}`}
                    alt={selectedPrinter.Name}
                    style={{ maxWidth: "100%", height: "50%", width: "50%" }}
                  />
                ) : (
                  "Image Not Available"
                )}
              </div>
            </Modal>
          </Box>
        )}
      </div>

      <Footerfile />
    </div>
  );
};

export default AddPrinterModel;
